// @flow

import * as React from 'react';
import classnames from 'classnames';
import {defineMessages, FormattedMessage} from 'react-intl';
import Box from 'sail/Box';
import Card from 'sail/Card';
import Icon from 'sail/Icon';
import * as Assets from 'sail/Icon/Assets';
import {Body} from 'sail/Text';
import Button from 'sail/Button';
import {UnstyledLink} from 'sail/UnstyledLink';
import {PressableCore} from 'sail/Pressable';
import {useVideoPlayer} from 'docs/hooks';
import {useVideos} from 'docs/data';
import type {VideoModel} from 'docs/data';
import './VideoPlayer.scss';
import * as PlayerIcons from './Icons';

const messages = defineMessages({
  viewGuide: {
    id: 'docsShell.toc.video',
    description:
      'This shows a on top of the video inviting users to click to play',
    defaultMessage: 'Watch the video guide',
  },
});

const VideoPlayerInner = ({
  video,
  isExpanded,
  setExpanded,
}: {
  video: VideoModel,
  isExpanded: boolean,
  setExpanded: (boolean | ((boolean) => boolean)) => void,
}) => {
  // States

  const [hasInitiatedPlayback, setHasInitiatedPlayback] = React.useState(false);
  const [isFocused, setFocused] = React.useState(false);

  const {frame, isPlaying, playVideo, pauseVideo} = useVideoPlayer({
    video,
  });

  React.useEffect(() => {
    if (isPlaying && !hasInitiatedPlayback) {
      setHasInitiatedPlayback(true);
      setExpanded(true);
    }
  }, [isPlaying, hasInitiatedPlayback, setExpanded]);

  // Actions

  const togglePlayback = React.useCallback(() => {
    if (isPlaying) {
      pauseVideo();
    } else {
      playVideo();
    }
  }, [isPlaying, playVideo, pauseVideo]);

  const onCoverClick = () => {
    togglePlayback();
  };

  const onExpandClick = () => {
    setExpanded((expanded) => !expanded);
  };

  React.useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (!isExpanded && !isFocused) {
        return;
      }

      if (event.code === 'Escape') {
        event.preventDefault();
        setExpanded(false);
        setFocused(false);
      } else if (event.code === 'Space') {
        event.preventDefault();
        togglePlayback();
      } else if (event.code === 'KeyF') {
        event.preventDefault();
        setExpanded((f) => !f);
      }
    };

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isExpanded, isFocused, togglePlayback, setExpanded]);

  const onFocus = (event) => {
    event.preventDefault();

    setFocused(true);
  };

  const onBlur = (event) => {
    event.preventDefault();

    setFocused(false);
  };

  return (
    <PressableCore
      onFocus={onFocus}
      onBlur={onBlur}
      shadow={{base: true, elavated: true, focused: isFocused}}
      radius="all"
      display="block"
      className={classnames(
        'VideoPlayer',
        isPlaying ? 'VideoPlayer--playing' : null,
        hasInitiatedPlayback ? 'VideoPlayer--initially-playback' : null,
      )}
    >
      <Box className="VideoPlayer-modal-click-layer" onClick={onExpandClick} />
      <Card className="VideoPlayer-shape">
        <Card className="VideoPlayer-frame" shadow="none">
          {frame}
        </Card>

        <Box className="VideoPlayer-controls-layer">
          <Card className="VideoPlayer-cover" shadow="none">
            {isPlaying && (
              <Box className="VideoPlayer-expander">
                <UnstyledLink onClick={onExpandClick}>
                  <PlayerIcons.Expand size={20} />
                </UnstyledLink>
              </Box>
            )}
            <UnstyledLink
              onClick={onCoverClick}
              className="VideoPlayer-cover-link"
            >
              <Icon
                icon={isPlaying ? Assets.pause : Assets.playCircle}
                size={32}
                color="white"
              />
              {!hasInitiatedPlayback && (
                <Box margin={{top: 8}}>
                  <Body align="center" color="white">
                    <FormattedMessage {...messages.viewGuide} />
                  </Body>
                </Box>
              )}
            </UnstyledLink>
          </Card>

          <Box className="VideoPlayer-corner-controls">
            <Button
              label="Mini Player"
              size="small"
              icon={PlayerIcons.MiniPlayerIcon}
              onClick={onExpandClick}
            />
          </Box>
        </Box>
      </Card>
    </PressableCore>
  );
};

export const VideoPlayer = () => {
  const {videoPlayerPosition, tocVideo} = useVideos();

  const [isBrowser, setBrowser] = React.useState(false);
  const [isExpanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    setBrowser(true);
  }, []);

  const styleProps = React.useMemo(() => {
    if (isExpanded) {
      return {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      };
    } else if (
      videoPlayerPosition &&
      videoPlayerPosition.top > 0 &&
      videoPlayerPosition.left > 0 &&
      videoPlayerPosition.width > 0 &&
      videoPlayerPosition.height > 0
    ) {
      return {
        top: videoPlayerPosition.top,
        left: videoPlayerPosition.left,
      };
    } else {
      return {display: 'none'};
    }
  }, [videoPlayerPosition, isExpanded]);

  if (!isBrowser || !tocVideo) {
    return null;
  }

  return (
    <Box
      className={classnames(
        'VideoPlayer-container',
        isExpanded ? 'VideoPlayer-container--expanded' : null,
      )}
      style={styleProps}
    >
      <VideoPlayerInner
        isExpanded={isExpanded}
        setExpanded={setExpanded}
        video={tocVideo}
      />
    </Box>
  );
};
